import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import './Calendario.css';

const Calendario = ({ entidadId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [recordatorios, setRecordatorios] = useState([]);
  
  const [titulo, setTitulo] = useState('');
  const [hora, setHora] = useState('12:00');
  const [descripcion, setDescripcion] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchRecordatorios = useCallback(async () => {
    try {
      const params = entidadId ? { entidadId } : {};
      const { data } = await api.get('/obtenerRecordatorios', { params });
      console.log('Recordatorios obtenidos:', data);
      
      let recordatoriosArray = [];
      
      if (Array.isArray(data)) {
        recordatoriosArray = data;
      } else if (data && Array.isArray(data.data)) {
        recordatoriosArray = data.data;
      } else if (data && typeof data === 'object') {
        recordatoriosArray = [data.data || data];
      }
      
      setRecordatorios(recordatoriosArray);
    } catch (err) {
      console.error('Error al cargar recordatorios:', err);
    }
  }, [entidadId]);

  useEffect(() => {
    fetchRecordatorios();
  }, [currentDate, fetchRecordatorios]);

  const handleAddRecordatorio = async (e) => {
    e.preventDefault();
    if (!titulo || !hora) return;

    const fYear = selectedDate.getFullYear();
    const fMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const fDay = String(selectedDate.getDate()).padStart(2, '0');
    const fechaFormateada = `${fYear}-${fMonth}-${fDay}`;
    const nuevoRecordatorio = entidadId
      ? { titulo, fecha: fechaFormateada, hora, descripcion, entidadId }
      : { titulo, fecha: fechaFormateada, hora, descripcion };

    setLoading(true);
    try {
      await api.post('/crearRecordatorio', nuevoRecordatorio);
      
      setTitulo('');
      setHora('12:00');
      setDescripcion('');
      setShowModal(false);
      
      fetchRecordatorios();
    } catch (err) {
      console.error('Error al agregar recordatorio:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecordatorio = async (id) => {
    try {
      await api.delete(`/eliminarRecordatorio`, { data: { id } });
      
      fetchRecordatorios();
    } catch (err) {
      console.error('Error al eliminar recordatorio:', err);
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  const firstDay = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const generateGrid = () => {
    const grid = [];
    const today = new Date();
    
    for (let i = 0; i < firstDay; i++) {
      grid.push(<div key={`empty-${i}`} className="calendario-day empty"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const dateForCell = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      
      const year = dateForCell.getFullYear();
      const month = String(dateForCell.getMonth() + 1).padStart(2, '0');
      const day = String(dateForCell.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;

      const isToday = localDateString === today.toLocaleDateString('en-CA');
      
      const sYear = selectedDate.getFullYear();
      const sMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const sDay = String(selectedDate.getDate()).padStart(2, '0');
      const sLocalDateString = `${sYear}-${sMonth}-${sDay}`;
      
      const isSelected = localDateString === sLocalDateString;
      
      const hasEvent = recordatorios.some(r => r.fecha && r.fecha.split('T')[0] === localDateString);

      grid.push(
        <div 
          key={i} 
          className={`calendario-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => setSelectedDate(dateForCell)}
        >
          {i}
          {hasEvent && <div className="event-indicator"></div>}
        </div>
      );
    }
    return grid;
  };

  const sYear = selectedDate.getFullYear();
  const sMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
  const sDay = String(selectedDate.getDate()).padStart(2, '0');
  const selectedDateString = `${sYear}-${sMonth}-${sDay}`;

  const dailyRecordatorios = recordatorios.filter(r => {
    if (!r.fecha) return false;
    const datePart = r.fecha.split('T')[0];
    return datePart === selectedDateString;
  });

  const parseTime = (timeStr) => {
    if (!timeStr) return { hours: 0, minutes: 0 };
    const [h, m] = timeStr.split(':').map(Number);
    return { hours: h || 0, minutes: m || 0 };
  };

  const { hours, minutes } = parseTime(hora);

  const incrementHours = useCallback(() => {
    const { hours: h } = parseTime(hora);
    const newHours = (h + 1) % 24;
    setHora(`${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
  }, [hora, minutes]);

  const decrementHours = useCallback(() => {
    const { hours: h } = parseTime(hora);
    const newHours = (h - 1 + 24) % 24;
    setHora(`${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
  }, [hora, minutes]);

  const incrementMinutes = useCallback(() => {
    const { minutes: m } = parseTime(hora);
    const newMinutes = (m + 1) % 60;
    setHora(`${String(hours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
  }, [hora, hours]);

  const decrementMinutes = useCallback(() => {
    const { minutes: m } = parseTime(hora);
    const newMinutes = (m - 1 + 60) % 60;
    setHora(`${String(hours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
  }, [hora, hours]);

  const incrementMinutes15 = useCallback(() => {
    const { minutes: m } = parseTime(hora);
    const newMinutes = (m + 15) % 60;
    setHora(`${String(hours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
  }, [hora, hours]);

  const decrementMinutes15 = useCallback(() => {
    const { minutes: m } = parseTime(hora);
    const newMinutes = (m - 15 + 60) % 60;
    setHora(`${String(hours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
  }, [hora, hours]);

  const formatDateLabel = (date) => {
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} de ${month} de ${year}`;
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Calendario y Recordatorios</h1>
        <p className="page-subtitle">Gestiona tus audiencias y compromisos</p>
      </div>

      <div className="calendario-container">
        <div className="calendario-grid-section">
          <div className="calendario-header">
            <button className="btn-nav" onClick={prevMonth}><i className="bi bi-chevron-left"></i></button>
            <h2>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            <button className="btn-nav" onClick={nextMonth}><i className="bi bi-chevron-right"></i></button>
          </div>
          
          <div className="calendario-grid">
            <div className="calendario-day-name">Dom</div>
            <div className="calendario-day-name">Lun</div>
            <div className="calendario-day-name">Mar</div>
            <div className="calendario-day-name">Mie</div>
            <div className="calendario-day-name">Jue</div>
            <div className="calendario-day-name">Vie</div>
            <div className="calendario-day-name">Sab</div>
            {generateGrid()}
          </div>
        </div>

        <div className="calendario-sidebar">
          <h3 className="sidebar-title">
            <i className="bi bi-calendar-event" style={{ marginRight: 8 }}></i>
            Eventos para el {selectedDate.getDate()} de {monthNames[selectedDate.getMonth()]}
          </h3>
          
          <div className="recordatorios-list">
            {dailyRecordatorios.length === 0 ? (
              <div className="no-events">No hay recordatorios para este dia.</div>
            ) : (
              dailyRecordatorios.map(rec => {
                const horaFormat = rec.hora ? rec.hora.substring(0, 5) : '';
                return (
                  <div key={rec.id} className="recordatorio-item">
                    <div className="recordatorio-info">
                      <h4>{rec.titulo}</h4>
                      <p><i className="bi bi-clock" style={{ marginRight: 4 }}></i>{horaFormat} {rec.descripcion && `- ${rec.descripcion}`}</p>
                    </div>
                    <button className="btn-delete" onClick={() => handleDeleteRecordatorio(rec.id)} title="Eliminar recordatorio">
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                );
              })
            )}
          </div>

          </div>

      <button className="fab-button" onClick={() => { setTitulo(''); setHora('12:00'); setDescripcion(''); setShowModal(true); }} title="Nuevo recordatorio">+</button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3><i className="bi bi-plus-circle" style={{ marginRight: 8 }}></i>Nuevo Recordatorio</h3>
            <p className="modal-date"><i className="bi bi-calendar3" style={{ marginRight: 6 }}></i>{formatDateLabel(selectedDate)}</p>
            <form className="recordatorio-form" onSubmit={handleAddRecordatorio}>
              <input 
                type="text" 
                placeholder="Titulo (ej: Audiencia)" 
                value={titulo} 
                onChange={(e) => setTitulo(e.target.value)} 
                required 
              />
              <div className="time-picker">
                <div className="time-picker-column">
                  <button type="button" className="time-picker-btn" onClick={incrementHours}><i className="bi bi-chevron-up"></i></button>
                  <span className="time-picker-value">{String(hours).padStart(2, '0')}</span>
                  <button type="button" className="time-picker-btn" onClick={decrementHours}><i className="bi bi-chevron-down"></i></button>
                </div>
                <span className="time-picker-separator">:</span>
                <div className="time-picker-column">
                  <button type="button" className="time-picker-btn-sm" onClick={incrementMinutes15}>+15</button>
                  <button type="button" className="time-picker-btn" onClick={incrementMinutes}><i className="bi bi-chevron-up"></i></button>
                  <span className="time-picker-value">{String(minutes).padStart(2, '0')}</span>
                  <button type="button" className="time-picker-btn" onClick={decrementMinutes}><i className="bi bi-chevron-down"></i></button>
                  <button type="button" className="time-picker-btn-sm" onClick={decrementMinutes15}>-15</button>
                </div>
              </div>
              <textarea 
                placeholder="Descripcion / Ubicacion (opcional)" 
                value={descripcion} 
                onChange={(e) => setDescripcion(e.target.value)} 
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? <><i className="bi bi-hourglass-split"></i> Guardando...</> : <><i className="bi bi-plus-lg"></i> Agregar Recordatorio</>}
              </button>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Calendario;
